import { duration } from '../format.ts';
import { useNow } from '../useNow.ts';
import { useSupply } from './sources.ts';
import styles from './states.module.css';

/* ==========================================================================
   THE IN-FLIGHT STATE OWES THE SAME EVIDENCE THE FAILURE STATE OWES.

   The console's failure chrome has carried a measured age since the first
   round, and it is the reason an operator trusts it:

     LINK DOWN  Nothing has reached this console for 26s · next attempt 1s
     no reply /api/robots · 4 lost · figures 30s old

   Its loading chrome carried nothing. `Reading…`, at 300 ms and at four
   minutes, is the same eleven pixels — measured on a stalled load, the entire
   1600×1000 frame changed by 163 pixels between t=300 ms and t=3 s and every
   one of them was the header clock. So a hung API renders a screen that says
   the console is working on it, for ever, with no way to tell that from a
   console that is working on it and about to finish.

   That is the whole of this component: the word, and then the duration, in the
   same ink and the same position, on the same 1 Hz beat as every other age on
   the surface.

   WHAT IT DELIBERATELY DOES NOT SAY
     · No attempt count. A first read that has failed even once is no longer
       `reading` — `useRead` holds the fault, the panel goes to `blank`, and
       the fault line below it prints the runs lost and the countdown to the
       next attempt. A count here would always be 1.
     · No percentage, no bar length, no "almost there". Nothing in an HTTP
       read knows how far through it is, and the reduced-motion regression this
       round fixed was exactly a bar that appeared to claim otherwise.

   THE FIGURE, AND WHOSE IT IS
     The shapes in Skeleton.tsx are drawn from a context that carries no query
     identity, so with no prop this reads the surface's oldest unanswered
     read — exact on a cold start (every source begins within a frame) and
     exact for a single slow endpoint under a healthy console (it is the only
     thing still opening). `ReadBody` holds the actual read and passes
     `elapsedMs` instead, so every panel that goes through it is per-panel.
   ========================================================================== */

export function ReadingNote({
  /** This panel's own wait, from `Read.waitingMs`. Omit and the surface's
      oldest unanswered read is used instead. */
  elapsedMs,
}: {
  elapsedMs?: number | null | undefined;
}) {
  const { openedAt } = useSupply();
  /* Its own clock rather than the caller's: this is one line of text at the
     first row's position, and re-rendering it once a second costs a text node
     while re-rendering the instrument around it would repaint a 28-column
     table. `useRead` already ticks, so a caller that passes `elapsedMs` is
     paying for this twice — harmless, and it keeps the component honest when
     the prop is absent. */
  const now = useNow(true);
  const own = elapsedMs ?? (openedAt === null ? null : Math.max(0, now - openedAt));

  return (
    <>
      Reading…
      {/* Withheld under a second. It would read `0s` for the whole of a load
          that is behaving normally, and a figure that appears, reads zero and
          vanishes again teaches an operator to ignore it.

          HIDDEN FROM THE ACCESSIBILITY TREE, AND THAT IS NOT AN OVERSIGHT.
          This line is rendered inside a `role="status"` while a read is in
          flight (Skeleton.tsx `Note`), so that the change from `Reading…` to
          `Nothing has been read.` is announced once. A duration ticking inside
          that region would be a fresh announcement every second for as long as
          the load lasted — the console shouting `Reading 1s`, `Reading 2s`,
          `Reading 3s` over whatever the operator was actually doing. The
          figure is for the eye; the accessible copy of it is on the panel's
          read tag, in its `title`, where it is read on demand. */}
      {own !== null && own >= 1_000 && (
        <span className={styles.waited} aria-hidden="true">
          {duration(own)}
        </span>
      )}
    </>
  );
}
